import type { ReactNode } from "react";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useSession, useSignOut } from "../lib/session.js";
import { ADMIN_ITEMS } from "./BottomNav.js";

function AdminNav({ onNavigate }: { onNavigate: () => void }) {
  return (
    <nav className="flex flex-col gap-1 p-4">
      {ADMIN_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end
          onClick={onNavigate}
          className={({ isActive }) =>
            `rounded-lg px-3 py-2 text-sm font-medium ${
              isActive ? "bg-brand-50 text-brand-700" : "text-stone-600 hover:bg-stone-100"
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Desktop backoffice shell for admin routes only — restaurant/supplier routes keep AppShell + BottomNav. */
export function AdminShell({ title, children }: { title: string; children: ReactNode }) {
  const { data: me } = useSession();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-dvh bg-stone-50 text-stone-900 md:flex">
      <aside className="hidden w-60 shrink-0 border-r border-stone-200 bg-white md:block">
        <div className="border-b border-stone-200 px-4 py-4 text-lg font-semibold">Dishour Admin</div>
        <AdminNav onNavigate={() => {}} />
      </aside>

      {sidebarOpen ? (
        <div className="fixed inset-0 z-20 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-60 bg-white shadow-xl">
            <div className="border-b border-stone-200 px-4 py-4 text-lg font-semibold">Dishour Admin</div>
            <AdminNav onNavigate={() => setSidebarOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex min-h-dvh flex-1 flex-col">
        <header className="app-header sticky top-0 z-10 flex items-center justify-between border-b border-stone-200 bg-white/90 px-4 py-3 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="메뉴 열기"
              className="rounded-lg border border-stone-300 px-2 py-1 text-sm md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>
            <h1 className="text-lg font-semibold">{title}</h1>
          </div>
          {me ? (
            <button
              type="button"
              className="text-sm text-stone-500 underline"
              onClick={() => signOut.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) })}
            >
              로그아웃
            </button>
          ) : null}
        </header>
        <main className="flex-1 px-4 py-4 md:px-6 md:py-6">{children}</main>
      </div>
    </div>
  );
}
